import {getLogger} from './logging'
import {
  pushDataLayerEvent,
  getOrCreateVisitorData,
  getCurrentPageInfo,
  createBaseEventData
} from './analyticsHelpers'
import {STANDARD_EVENTS} from './analyticsInterfaces'

const logger = getLogger('interactionAnalytics')

/**
 * Tracks profile editing interactions
 */
export function trackProfileEdit(
  action: 'view_profile' | 'edit_profile' | 'save_profile' | 'cancel_edit',
  field?: 'email' | 'phone' | 'name' | 'preferences',
  userId?: string
): void {
  try {
    const baseData = createBaseEventData(userId)

    const event = {
      event: STANDARD_EVENTS.PROFILE_EDIT,
      ...baseData,
      action,
      field_modified: field
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking profile edit',
      data: {error}
    })
  }
}

/**
 * Tracks booking management interactions
 */
export function trackBookingManagement(
  action:
    | 'view_booking'
    | 'modify_booking'
    | 'modify_booking_initiated'
    | 'modify_recap_viewed'
    | 'modify_booking_completed'
    | 'modify_booking_failed'
    | 'cancel_booking_initiated'
    | 'cancel_booking_completed'
    | 'cancel_booking_cancelled',
  reservationId?: string,
  cancellationReason?: string,
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_booking_management',
      action,
      reservation_id: reservationId,
      cancellation_reason: cancellationReason,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking booking management',
      data: {error}
    })
  }
}

/**
 * Tracks payment method interactions
 */
export function trackPaymentUpdate(
  action:
    | 'view_payment_methods'
    | 'add_payment_method'
    | 'update_payment_method'
    | 'remove_payment_method'
    | 'payment_method_success'
    | 'payment_method_error',
  paymentMethodType?: 'card' | 'bank_account' | 'digital_wallet',
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_payment_update',
      action,
      payment_method_type: paymentMethodType,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking payment update',
      data: {error}
    })
  }
}

/**
 * Tracks communication preferences changes
 */
export function trackCommunicationPreferences(
  action: 'view_preferences' | 'update_preferences',
  preferenceType: 'email' | 'sms' | 'marketing_email' | 'reservation_updates',
  newValue: 'opt_in' | 'opt_out' | 'both' | 'email_only',
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_communication_preferences',
      action,
      preference_type: preferenceType,
      new_value: newValue,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking communication preferences',
      data: {error}
    })
  }
}

/**
 * Tracks check-in page interactions
 */
export function trackCheckinPage(
  action:
    | 'view_checkin_page'
    | 'start_checkin'
    | 'complete_checkin'
    | 'checkin_error',
  step?:
    | 'welcome'
    | 'identity_verification'
    | 'payment_verification'
    | 'room_assignment'
    | 'complete',
  errorType?: string,
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_checkin',
      action,
      checkin_step: step,
      error_type: errorType,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking check-in',
      data: {error}
    })
  }
}

/**
 * Tracks form submission events
 */
export function trackFormSubmission(
  formType:
    | 'profile_edit'
    | 'booking_cancel'
    | 'booking_modify'
    | 'payment_update'
    | 'communication_preferences'
    | 'checkin_form',
  action:
    | 'form_started'
    | 'form_submitted'
    | 'form_success'
    | 'form_error'
    | 'form_cancelled',
  fieldErrors?: string[],
  userId?: string
): void {
  try {
    const visitorData = getOrCreateVisitorData()

    const event = {
      event: 'guest_portal_form_submission',
      form_type: formType,
      action,
      field_errors: fieldErrors,
      current_page: getCurrentPageInfo().pathname,
      current_page_title: getCurrentPageInfo().title,
      session_id: visitorData.sessionId,
      user_id: userId,
      timestamp: new Date().toISOString()
    }

    pushDataLayerEvent(event)
  } catch (error) {
    logger.error({
      message: 'Error tracking form submission',
      data: {error}
    })
  }
}
